import { getAllAppointmentSlots, lockSlot, releaseLock, initiateBookingWithPayment } from "../../../../redux/member-slices/appointments.slice";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AptCalender from "./AptCalender";
import Icons from "../../../../assets/js/Icons";
import SlotPrev from "./SlotPrev";
import moment from "moment-timezone";
import TimeUtils from "../../../../utils/time.utils";
import { formatPrice, formatLocalPrice } from "../../../../utils/helper";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";



// ────────────────────────────────────────────────────────
// STEPS
const STEP_PICK = "pick";       // Pick date & slot
const STEP_CONFIRM = "confirm"; // Review & confirm
// ────────────────────────────────────────────────────────

function CreateAppointment({ handleClose }) {
  const dispatch = useDispatch();
  const { data } = useSelector((s) => s.otherSlice);

  const specialistId = data?.specialistId;
  const userTz = TimeUtils.getCurrentTimezone();

  // UI state
  const [step, setStep] = useState(STEP_PICK);
  const [isLocking, setIsLocking] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isFetchingSlots, setIsFetchingSlots] = useState(false);
  const [slots, setSlots] = useState([]);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [lockData, setLockData] = useState(null);      // { lockId, expiresAt, expiresInSeconds }
  const [countdown, setCountdown] = useState(null);    // seconds remaining
  const [error, setError] = useState(null);

  const countdownRef = useRef(null);

  // ── Countdown ticker ──────────────────────────────────
  useEffect(() => {
    if (step !== STEP_CONFIRM || !lockData?.expiresAt) return;

    const tick = () => {
      const remaining = Math.max(
        0,
        Math.round((new Date(lockData.expiresAt) - Date.now()) / 1000)
      );
      setCountdown(remaining);
      if (remaining === 0) {
        clearInterval(countdownRef.current);
        setError("Your slot hold expired. Please go back and select the slot again.");
      }
    };

    tick();
    countdownRef.current = setInterval(tick, 1000);
    return () => clearInterval(countdownRef.current);
  }, [step, lockData]);

  useEffect(() => {
    const today = moment().format("YYYY-MM-DD");

    setSelectedDate(today);

    handleDateChange(today, true);
  }, []);

  // ── Load slots when a date is selected ───────────────
  async function handleDateChange(date, hasSlots) {
    setSelectedDate(date);
    setSelectedSlot(null);
    setSlots([]);
    setExchangeRate(null);
    setError(null);

    if (!hasSlots) return;

    setIsFetchingSlots(true);
    try {
      const res = await dispatch(
        getAllAppointmentSlots({ date, specialistId })
      ).unwrap();
      if (res?.data?.slots) setSlots(res.data.slots);
      if (res?.data?.exchangeRate) setExchangeRate(res.data.exchangeRate);

    } finally {
      setIsFetchingSlots(false);
    }
  }

  // ── Slot chip click ───────────────────────────────────
  function handleSelectSlot(slot) {
    setSelectedSlot(slot);
    setError(null);
  }

  // ── Step 1 → Step 2: Lock the slot ───────────────────
  async function handleReserve() {
    if (!selectedSlot) {
      setError("Please select a time slot first.");
      return;
    }

    setError(null);
    setIsLocking(true);
    try {
      const res = await dispatch(
        lockSlot({
          specialistId,
          slotId: selectedSlot.slotId,   // identity key from availability response
          date: selectedDate,            // "YYYY-MM-DD" already in state
          timezone: userTz,
        })
      ).unwrap();
      if (res?.data?.lockId) {
        setLockData(res.data);
        setStep(STEP_CONFIRM);
      }
    } catch (err) {
      setError(err?.message || "Could not reserve slot. Please try again.");
    } finally {
      setIsLocking(false);
    }
  }

  // ── Step 2: Confirm booking & Redirect to Stripe ───────────────────────────
  async function handleConfirm() {
    if (!lockData?.lockId) return;
    setError(null);
    setIsConfirming(true);
    try {
      const res = await dispatch(
        initiateBookingWithPayment({
          specialistId,
          lockId: lockData.lockId,
          notes,
          timezone: userTz,
          termsAccepted,
        })
      ).unwrap();
      const checkoutUrl = res?.data?.checkoutUrl;

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        throw new Error("Missing checkout URL from server.");
      }
    } catch (err) {
      setError(err?.message || "Booking failed. Please try again.");
    } finally {
      setIsConfirming(false);
    }
  }

  // ── Back to slot selection ────────────────────────────
  function handleDismiss() {
    if (lockData?.lockId) {
      dispatch(releaseLock({ lockId: lockData.lockId, specialistId, slotId: selectedSlot?.slotId, date: selectedDate }));
    }
    if (handleClose) handleClose();
  }

  function handleBack() {
    if (lockData?.lockId) {
      dispatch(releaseLock({ lockId: lockData.lockId, specialistId, slotId: selectedSlot?.slotId, date: selectedDate }));
    }
    clearInterval(countdownRef.current);
    setLockData(null);
    setCountdown(null);
    setError(null);
    setStep(STEP_PICK);
  }

  const lockExpired = countdown === 0;

  // ── CONFIRM STEP ──────────────────────────────────────
  if (step === STEP_CONFIRM) {
    const startLocal = moment.utc(selectedSlot?.startTime).tz(userTz);
    const endLocal = moment.utc(selectedSlot?.endTime).tz(userTz);
    const exchangeValues = exchangeRate?.rate || 1;

    const mm = Math.floor(countdown / 60);
    const ss = String(countdown % 60).padStart(2, "0");

    return (
      <Paper>
        <DialogTitle sx={{ position: "relative", p: 1 }}>
          <Typography variant="dialogTitle">Confirm Appointment</Typography>
          <IconButton size="small" variant="dialogCloseBtn" onClick={handleDismiss}>
            <Icons.Cross />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            {/* Booking summary */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "primary.50",
                border: "1px solid",
                borderColor: "primary.100",
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
                Your appointment
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {startLocal.format("dddd, MMMM D, YYYY")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {startLocal.format("hh:mm A")} – {endLocal.format("hh:mm A")}{" "}
                ({userTz})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Duration: {selectedSlot?.duration} min
              </Typography>
              <Typography variant="body2" fontWeight={600} mt={1}>
                Price: {formatLocalPrice(selectedSlot?.price, exchangeValues, selectedSlot?.currency)}
              </Typography>
            </Box>

            {/* Countdown  */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                size="small"
                color={lockExpired ? "error" : countdown <= 60 ? "warning" : "success"}
                label={
                  lockExpired
                    ? "Hold expired"
                    : `Slot held for ${mm}:${ss}`
                }
              />
              <Typography variant="caption" color="text.secondary">
                Confirm before the timer runs out
              </Typography>
            </Stack>

            {/* Terms acceptance */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{" "}
                  <Link
                    to="/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#1976d2", textDecoration: "underline" }}
                  >
                    Terms and Conditions
                  </Link>
                  {" "}for booking this appointment
                </Typography>
              }
            />

            {/* Notes */}
            <TextField
              multiline
              minRows={3}
              maxRows={5}
              fullWidth
              size="small"
              label="Notes (optional)"
              placeholder="Share anything helpful for the specialist..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            {/* Error */}
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ gap: 1 }}>
          <Button
            size="small"
            variant="text"
            onClick={handleBack}
            disabled={isConfirming}
          >
            Back
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirm}
            disabled={isConfirming || lockExpired || !termsAccepted}
            startIcon={isConfirming && <CircularProgress size={14} />}
          >
            {isConfirming ? "Booking..." : "Confirm Booking"}
          </Button>
        </DialogActions>
      </Paper>
    );
  }

  // ── PICK DATE + SLOT STEP (default) ──────────────────
  return (
    <Paper>
      <DialogTitle sx={{ position: "relative", p: 1 }}>
        <Typography variant="dialogTitle">Schedule Appointment</Typography>
        <IconButton size="small" variant="dialogCloseBtn" onClick={handleDismiss}>
          <Icons.Cross />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          ".MuiInputBase-root": { borderRadius: "6px" },
          textarea: { resize: "none" },
        }}
      >
        {/* Calendar */}
        <AptCalender onSelect={handleDateChange} />

        {/* Legend */}
        <Stack spacing={1} mt={2} direction="row" divider={<Divider sx={{ borderLeftWidth: "thin" }} flexItem />}>
          {[
            { color: "blue.400", type: "Selected" },
            { color: "orange.400", type: "Today" },
            { color: "green.400", type: "Available" },
          ].map((element) => (
            <Stack key={element.type} spacing={0.5} direction="row" alignItems="center">
              <Box
                sx={{
                  height: 14,
                  aspectRatio: "1/1",
                  bgcolor: element.color,
                  borderRadius: 1,
                }}
              />
              <Typography variant="body2">{element.type}</Typography>
            </Stack>
          ))}
        </Stack>

        {/* Slots */}
        {isFetchingSlots ? (
          <Stack direction="row" justifyContent="center" mt={2}>
            <CircularProgress size={22} />
          </Stack>
        ) : (
          <SlotPrev slots={slots} handleSelectSlot={handleSelectSlot} exchangeRate={exchangeRate} />
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          size="small"
          variant="outlined"
          onClick={handleReserve}
          disabled={!selectedSlot || isLocking}
          startIcon={isLocking && <CircularProgress size={14} />}
        >
          {isLocking ? "Booking Appointment..." : "Book Appointment →"}
        </Button>
      </DialogActions>
    </Paper>
  );
}

export default CreateAppointment;
